import { computed, ref, watch } from "vue";
import { useBp } from "@/utils/breakpoints";
import { useShellVisibility } from "@/composables/useShellVisibility";

// Docked-SidePanel state, shared between App.vue (which renders the panel)
// and the action bars (which host the collapse/expand toggle).
const isExpanded = ref(localStorage.getItem("sidepanel-expanded") !== "false");
watch(isExpanded, (expanded) => {
  localStorage.setItem("sidepanel-expanded", String(expanded));
});

export const MIN_DOCK_WIDTH_PX = 320;
// The docked panel's default width before the user ever drags it — mirrors
// the original w-[18%] of the layout row (viewport minus the left sidebar).
const DEFAULT_DOCK_WIDTH = "calc(0.18 * (100vw - var(--sidebar-width, 0px)))";
const DOCK_WIDTH_STORAGE_KEY = "sidepanel-dock-width";

const storedDockWidth = Number(
  localStorage.getItem(DOCK_WIDTH_STORAGE_KEY) ?? NaN,
);
// null = default 18% width; a number = user-chosen px width.
const dockWidthPx = ref<number | null>(
  Number.isFinite(storedDockWidth) ? storedDockWidth : null,
);
const isDockResizing = ref(false);
// Ungates the shell bars' easing mid-drag (see PageFrame.vue).
watch(isDockResizing, (resizing) => {
  document.documentElement.toggleAttribute("data-dock-resizing", resizing);
});

export function persistDockWidth(): void {
  const px = dockWidthPx.value;
  if (px === null) {
    localStorage.removeItem(DOCK_WIDTH_STORAGE_KEY);
  } else {
    localStorage.setItem(DOCK_WIDTH_STORAGE_KEY, String(Math.round(px)));
  }
}
// A drag would otherwise put a synchronous localStorage write on the same
// main thread as each frame's layout pass; it persists once on teardown.
watch(dockWidthPx, () => {
  if (!isDockResizing.value) persistDockWidth();
});
// CSS clamp keeps a persisted width sane across viewport changes (e.g. a
// width chosen on a 4k monitor reopened on a laptop) without JS listeners.
// The drag path builds this too: a raw px value there would disagree with
// 35vw by the scrollbar width and shimmer at the seam.
export function clampedDockWidth(px: number): string {
  return `clamp(${MIN_DOCK_WIDTH_PX}px, ${Math.round(px)}px, 35vw)`;
}
const dockWidth = computed(() =>
  dockWidthPx.value === null
    ? DEFAULT_DOCK_WIDTH
    : clampedDockWidth(dockWidthPx.value),
);

// The one width every shell element reads: the panel, the fixed bars and the
// popup overlay. One document write keeps every edge in the same paint.
export function writeSidepanelWidth(docked: boolean, width: string): void {
  document.documentElement.style.setProperty(
    "--sidepanel-width",
    docked ? width : "0px",
  );
}

export function useSidepanelDock() {
  const bp = useBp();
  const { shouldShowSidepanel } = useShellVisibility();

  const canDock = computed(
    () => shouldShowSidepanel.value && !bp.smaller["3xl"],
  );
  const isDocked = computed(() => canDock.value && isExpanded.value);

  return {
    isExpanded,
    canDock,
    isDocked,
    dockWidth,
    dockWidthPx,
    isDockResizing,
  };
}
